// src/services/indexedDB/utils/date-helpers.ts
// 日期處理工具函數

/**
 * 將日期轉換為統一的日期字符串格式 (YYYY-MM-DD)
 * 用於 IndexedDB 索引查詢
 *
 * @param date - 日期物件
 * @returns 格式化的日期字符串
 */
export const formatDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 將各種日期格式標準化為 Date 物件
 *
 * @param value - 日期值（Date, number, string）
 * @returns Date 物件
 */
export const normalizeDate = (value: Date | number | string): Date => {
  if (value instanceof Date) {
    return value;
  }
  return new Date(value);
};

/**
 * 將日期轉換為時間戳（可序列化）
 *
 * @param date - 日期值
 * @returns 時間戳（毫秒）
 */
export const toTimestamp = (date: Date | number | string | undefined): number | undefined => {
  if (date === undefined) return undefined;
  if (typeof date === 'number') return date;
  if (date instanceof Date) return date.getTime();
  return new Date(date).getTime();
};

/**
 * 將時間戳轉換為 Date 物件
 *
 * @param timestamp - 時間戳（毫秒）
 * @returns Date 物件或 undefined
 */
export const fromTimestamp = (timestamp: number | undefined): Date | undefined => {
  if (timestamp === undefined) return undefined;
  return new Date(timestamp);
};

/**
 * 檢查日期是否在指定天數之前
 *
 * @param date - 要檢查的日期
 * @param days - 天數
 * @returns 是否過期
 */
export const isOlderThan = (date: Date | number, days: number): boolean => {
  const timestamp = typeof date === 'number' ? date : date.getTime();
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return timestamp < cutoff;
};

/**
 * 獲取指定天數前的日期
 *
 * @param days - 天數
 * @returns Date 物件
 */
export const daysAgo = (days: number): Date => {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000);
};

/**
 * 序列化日期欄位為時間戳
 * 用於儲存到 IndexedDB 前的處理
 *
 * @param obj - 包含日期欄位的物件
 * @param dateFields - 需要序列化的日期欄位名稱陣列
 * @returns 序列化後的物件
 */
export const serializeDateFields = <T extends Record<string, any>>(
  obj: T,
  dateFields: (keyof T)[]
): T => {
  const result = { ...obj };

  for (const field of dateFields) {
    if (result[field]) {
      result[field] = toTimestamp(result[field] as any) as any;
    }
  }

  return result;
};

/**
 * 反序列化日期欄位為 Date 物件
 * 用於從 IndexedDB 讀取後的處理
 *
 * @param obj - 包含時間戳欄位的物件
 * @param dateFields - 需要反序列化的日期欄位名稱陣列
 * @returns 反序列化後的物件
 */
export const deserializeDateFields = <T extends Record<string, any>>(
  obj: T,
  dateFields: (keyof T)[]
): T => {
  const result = { ...obj };

  for (const field of dateFields) {
    if (result[field] !== undefined && result[field] !== null) {
      result[field] = fromTimestamp(result[field] as any) as any;
    }
  }

  return result;
};
